import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Grid, Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, FormControl, InputLabel, Select, MenuItem, Tooltip, CircularProgress,
  Snackbar, Alert, Chip, Card, CardContent, Divider, alpha, Avatar,
} from '@mui/material';
import {
  Add as AddIcon, Refresh as RefreshIcon, Close as CloseIcon, Search as SearchIcon,
  Assignment as RequisitionIcon, Send as SendIcon, Edit as EditIcon, Visibility as ViewIcon,
  Person as PersonIcon, Inventory as InventoryIcon, Delete as DeleteIcon,
  Schedule as ScheduleIcon, LocalShipping as ShipIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { getApiUrl } from '../../config/environment';
import { usePageBreakpoints, pageContainerSx } from '../../hooks/useClinicPageBreakpoints';

import { useAuth } from '../../contexts/AuthContext';

const STATUS_MAP: Record<string, { label: string; color: string; bg: string }> = {
  pending: { label: 'Pendente', color: '#d97706', bg: '#fef3c7' },
  approved: { label: 'Aprovada', color: '#059669', bg: '#d1fae5' },
  partially_fulfilled: { label: 'Parcialmente Atendida', color: '#2563eb', bg: '#dbeafe' },
  fulfilled: { label: 'Atendida', color: '#059669', bg: '#dcfce7' },
  cancelled: { label: 'Cancelada', color: '#dc2626', bg: '#fef2f2' },
};

const PRIORITY_MAP: Record<string, { label: string; color: string; bg: string }> = {
  low: { label: 'Baixa', color: '#64748b', bg: '#f8fafc' },
  normal: { label: 'Normal', color: '#2563eb', bg: '#eff6ff' },
  high: { label: 'Alta', color: '#ea580c', bg: '#fff7ed' },
  urgent: { label: 'Urgente', color: '#dc2626', bg: '#fef2f2' },
};

const formatDate = (d: string) => d ? new Date(d).toLocaleDateString('pt-BR') : '-';
const formatDateTime = (d: string) => d ? new Date(d).toLocaleString('pt-BR') : '-';

interface RequisitionItem {
  product_name: string;
  quantity: number;
  unit: string;
  status: string;
  transferred_qty: number;
  unit_value: number;
  notes: string;
}

interface Interaction {
  id: string;
  user_name: string;
  message: string;
  created_at: string;
}

interface Requisition {
  id: string;
  number: number;
  requester_name: string;
  destination_name: string;
  destination_location: string;
  priority: string;
  status: string;
  deadline: string;
  items: RequisitionItem[];
  interactions: Interaction[];
  created_at: string;
  updated_at: string;
}

const InventoryRequisitions: React.FC = () => {
  const { isMobile } = usePageBreakpoints();

  const { user } = useAuth();
  const [requisitions, setRequisitions] = useState<Requisition[]>([]);
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<any[]>([]);
  const [locations, setLocations] = useState<any[]>([]);

  const [filterStatus, setFilterStatus] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [filterDeadline, setFilterDeadline] = useState('');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedReq, setSelectedReq] = useState<Requisition | null>(null);
  const [saving, setSaving] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  const [form, setForm] = useState({
    requester_name: user?.full_name || '',
    priority: 'normal',
    status: 'pending',
    deadline: '',
    destination_name: '',
    destination_location: '',
    items: [{ product_name: '', quantity: 1, unit: 'unidade', notes: '' }] as { product_name: string; quantity: number; unit: string; notes: string }[],
  });

  const fetchRequisitions = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (filterStatus) params.status = filterStatus;
      if (filterPriority) params.priority = filterPriority;
      if (filterDeadline) params.deadline_until = filterDeadline;
      const res = await axios.get(getApiUrl('/inventory-module/requisitions'), { params });
      setRequisitions(res.data.data || []);
    } catch {
      setRequisitions([]);
    } finally { setLoading(false); }
  }, [filterStatus, filterPriority, filterDeadline]);

  const fetchMeta = useCallback(async () => {
    try {
      const [itemsRes, locsRes] = await Promise.all([
        axios.get(getApiUrl('/inventory-module/items'), { params: { limit: 500 } }),
        axios.get(getApiUrl('/inventory-module/locations')),
      ]);
      setItems(itemsRes.data.data || []);
      setLocations(locsRes.data.data || []);
    } catch { }
  }, []);

  useEffect(() => { fetchMeta(); }, [fetchMeta]);
  useEffect(() => { fetchRequisitions(); }, [fetchRequisitions]);

  const handleSave = async () => {
    if (!form.requester_name.trim()) { setSnackbar({ open: true, message: 'Solicitante obrigatório', severity: 'error' }); return; }
    if (form.items.every(i => !i.product_name.trim())) { setSnackbar({ open: true, message: 'Adicione pelo menos um produto', severity: 'error' }); return; }
    try {
      setSaving(true);
      const payload = {
        ...form,
        items: form.items.filter(i => i.product_name.trim()),
      };
      await axios.post(getApiUrl('/inventory-module/requisitions'), payload);
      setSnackbar({ open: true, message: 'Requisição criada com sucesso!', severity: 'success' });
      setDialogOpen(false);
      resetForm();
      fetchRequisitions();
    } catch (e: any) {
      setSnackbar({ open: true, message: e.response?.data?.error || 'Erro ao criar requisição', severity: 'error' });
    } finally { setSaving(false); }
  };

  const handleStatusChange = async (id: string, newStatus: string) => {
    try {
      await axios.put(getApiUrl(`/inventory-module/requisitions/${id}`), { status: newStatus });
      setSnackbar({ open: true, message: 'Status atualizado', severity: 'success' });
      fetchRequisitions();
      if (selectedReq?.id === id) {
        setSelectedReq({ ...selectedReq, status: newStatus });
      }
    } catch { setSnackbar({ open: true, message: 'Erro ao atualizar', severity: 'error' }); }
  };

  const handleSendInteraction = async () => {
    if (!selectedReq || !newMessage.trim()) return;
    try {
      await axios.post(getApiUrl(`/inventory-module/requisitions/${selectedReq.id}/interactions`), {
        user_name: user?.full_name || 'Usuário',
        message: newMessage,
      });
      setNewMessage('');
      const updated = { ...selectedReq };
      updated.interactions = [...(updated.interactions || []), { id: Date.now().toString(), user_name: user?.full_name || 'Usuário', message: newMessage, created_at: new Date().toISOString() }];
      setSelectedReq(updated);
    } catch { }
  };

  const handleDeleteRequisition = async (id: string) => {
    if (!window.confirm('Deseja excluir esta requisição?')) return;
    try {
      await axios.delete(getApiUrl(`/inventory-module/requisitions/${id}`));
      setSnackbar({ open: true, message: 'Requisição excluída', severity: 'success' });
      fetchRequisitions();
      if (selectedReq?.id === id) setDetailOpen(false);
    } catch { setSnackbar({ open: true, message: 'Erro ao excluir', severity: 'error' }); }
  };

  const resetForm = () => setForm({
    requester_name: user?.full_name || '', priority: 'normal', status: 'pending', deadline: '',
    destination_name: '', destination_location: '',
    items: [{ product_name: '', quantity: 1, unit: 'unidade', notes: '' }],
  });

  const addFormItem = () => setForm({ ...form, items: [...form.items, { product_name: '', quantity: 1, unit: 'unidade', notes: '' }] });
  const removeFormItem = (idx: number) => {
    if (form.items.length <= 1) return;
    setForm({ ...form, items: form.items.filter((_, i) => i !== idx) });
  };
  const updateFormItem = (idx: number, field: string, value: any) => {
    const updated = [...form.items];
    (updated[idx] as any)[field] = value;
    setForm({ ...form, items: updated });
  };

  const totalReqs = requisitions.length;
  const pendingReqs = requisitions.filter(r => r.status === 'pending').length;

  return (
    <Box sx={pageContainerSx}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <RequisitionIcon sx={{ fontSize: 32, color: '#8b5cf6' }} />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>Requisição de Estoque</Typography>
            <Typography variant="body2" sx={{ color: '#94a3b8' }}>Solicitações internas de materiais e produtos</Typography>
          </Box>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => { resetForm(); setDialogOpen(true); }}
          sx={{ bgcolor: '#8b5cf6', '&:hover': { bgcolor: '#7c3aed' }, textTransform: 'none', fontWeight: 600 }}>
          Nova Requisição
        </Button>
      </Box>

      {/* Filtros */}
      <Paper sx={{ borderRadius: 2, p: 2, mb: 2.5 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={6} sm={3}>
            <FormControl size="small" fullWidth>
              <InputLabel>Status</InputLabel>
              <Select value={filterStatus} label="Status" onChange={(e) => setFilterStatus(e.target.value)}>
                <MenuItem value="">Todos</MenuItem>
                {Object.entries(STATUS_MAP).map(([k, v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={3}>
            <FormControl size="small" fullWidth>
              <InputLabel>Prioridade</InputLabel>
              <Select value={filterPriority} label="Prioridade" onChange={(e) => setFilterPriority(e.target.value)}>
                <MenuItem value="">Todas</MenuItem>
                {Object.entries(PRIORITY_MAP).map(([k, v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField size="small" fullWidth label="Prazo até" type="date" value={filterDeadline}
              onChange={(e) => setFilterDeadline(e.target.value)} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={6} sm={3} sx={{ display: 'flex', gap: 1 }}>
            <Button variant="contained" startIcon={<SearchIcon />} onClick={fetchRequisitions}
              sx={{ bgcolor: '#059669', '&:hover': { bgcolor: '#047857' }, textTransform: 'none', fontWeight: 600, flex: 1 }}>
              Buscar
            </Button>
            <Tooltip title="Atualizar"><IconButton onClick={fetchRequisitions}><RefreshIcon /></IconButton></Tooltip>
          </Grid>
        </Grid>
      </Paper>

      {/* Lista */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress sx={{ color: '#8b5cf6' }} /></Box>
        ) : requisitions.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6, color: '#94a3b8' }}>
            <RequisitionIcon sx={{ fontSize: 48, opacity: 0.4, mb: 1 }} />
            <Typography>Nenhuma requisição encontrada</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f9fafb' }}>
                  <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Abertura</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Prazo</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Prioridade</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Solicitante</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Produtos</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Localização destino</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {requisitions.map((req) => {
                  const st = STATUS_MAP[req.status] || STATUS_MAP.pending;
                  const pr = PRIORITY_MAP[req.priority] || PRIORITY_MAP.normal;
                  return (
                    <TableRow key={req.id} hover sx={{ cursor: 'pointer' }} onClick={() => { setSelectedReq(req); setDetailOpen(true); }}>
                      <TableCell><Typography variant="body2" sx={{ fontWeight: 600 }}>#{req.number || req.id?.slice(0, 6)}</Typography></TableCell>
                      <TableCell><Typography variant="body2">{formatDateTime(req.created_at)}</Typography></TableCell>
                      <TableCell><Typography variant="body2">{formatDate(req.deadline)}</Typography></TableCell>
                      <TableCell>
                        <Chip label={st.label} size="small" sx={{ fontWeight: 600, fontSize: '0.72rem', bgcolor: st.bg, color: st.color, border: `1px solid ${alpha(st.color, 0.2)}` }} />
                      </TableCell>
                      <TableCell>
                        <Chip label={pr.label} size="small" sx={{ fontWeight: 600, fontSize: '0.72rem', bgcolor: pr.bg, color: pr.color, border: `1px solid ${alpha(pr.color, 0.2)}` }} />
                      </TableCell>
                      <TableCell><Typography variant="body2">{req.requester_name}</Typography></TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {(req.items || []).map(i => `${i.product_name} (${i.quantity})`).join(', ') || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell><Typography variant="body2">{req.destination_location || '-'}</Typography></TableCell>
                      <TableCell align="center" onClick={e => e.stopPropagation()}>
                        <Tooltip title="Ver detalhes"><IconButton size="small" onClick={() => { setSelectedReq(req); setDetailOpen(true); }}><ViewIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
                        <Tooltip title="Excluir"><IconButton size="small" onClick={() => handleDeleteRequisition(req.id)}><DeleteIcon sx={{ fontSize: 16, color: '#ef4444' }} /></IconButton></Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        <Box sx={{ p: 1.5, borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="caption" color="text.secondary">{totalReqs} requisição(ões) — {pendingReqs} pendente(s)</Typography>
        </Box>
      </Paper>

      {/* ──── Dialog Nova Requisição ──── */}
      <Dialog fullScreen={isMobile} scroll="paper" open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <RequisitionIcon sx={{ color: '#8b5cf6' }} />
            Nova Requisição
          </Box>
          <IconButton onClick={() => setDialogOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth required size="small" label="Solicitante *" value={form.requester_name}
                onChange={(e) => setForm({ ...form, requester_name: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small"><InputLabel>Prioridade</InputLabel>
                <Select value={form.priority} label="Prioridade" onChange={(e) => setForm({ ...form, priority: e.target.value })}>
                  {Object.entries(PRIORITY_MAP).map(([k, v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth size="small" label="Data Prazo" type="date" value={form.deadline}
                onChange={(e) => setForm({ ...form, deadline: e.target.value })} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small"><InputLabel>Localização destino</InputLabel>
                <Select value={form.destination_location} label="Localização destino"
                  onChange={(e) => setForm({ ...form, destination_location: e.target.value })}>
                  <MenuItem value="">Selecione</MenuItem>
                  {locations.filter(l => l.is_active).map(l => <MenuItem key={l.id} value={l.name}>{l.name}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Destinatário" value={form.destination_name}
                onChange={(e) => setForm({ ...form, destination_name: e.target.value })} />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }}><Chip label="Produtos" size="small" icon={<InventoryIcon sx={{ fontSize: 16 }} />} sx={{ fontWeight: 600 }} /></Divider>
            </Grid>

            {form.items.map((item, idx) => (
              <React.Fragment key={idx}>
                <Grid item xs={12} sm={5}>
                  <FormControl fullWidth size="small"><InputLabel>Produto *</InputLabel>
                    <Select value={item.product_name} label="Produto *"
                      onChange={(e) => updateFormItem(idx, 'product_name', e.target.value)}>
                      <MenuItem value="">Selecione</MenuItem>
                      {items.map(i => <MenuItem key={i.id} value={i.name}>{i.name} {i.sku ? `(${i.sku})` : ''}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={4} sm={2}>
                  <TextField fullWidth size="small" label="Qtd" type="number" value={item.quantity}
                    onChange={(e) => updateFormItem(idx, 'quantity', Number(e.target.value))}
                    inputProps={{ min: 1 }} />
                </Grid>
                <Grid item xs={4} sm={2}>
                  <FormControl fullWidth size="small"><InputLabel>Unidade</InputLabel>
                    <Select value={item.unit} label="Unidade" onChange={(e) => updateFormItem(idx, 'unit', e.target.value)}>
                      <MenuItem value="unidade">Unidade</MenuItem>
                      <MenuItem value="caixa">Caixa</MenuItem>
                      <MenuItem value="pacote">Pacote</MenuItem>
                      <MenuItem value="frasco">Frasco</MenuItem>
                      <MenuItem value="ampola">Ampola</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={3} sm={2}>
                  <TextField fullWidth size="small" label="Obs" value={item.notes}
                    onChange={(e) => updateFormItem(idx, 'notes', e.target.value)} />
                </Grid>
                <Grid item xs={1} sx={{ display: 'flex', alignItems: 'center' }}>
                  <IconButton size="small" onClick={() => removeFormItem(idx)} disabled={form.items.length <= 1}
                    sx={{ color: '#ef4444' }}><DeleteIcon sx={{ fontSize: 18 }} /></IconButton>
                </Grid>
              </React.Fragment>
            ))}
            <Grid item xs={12}>
              <Button size="small" startIcon={<AddIcon />} onClick={addFormItem}
                sx={{ textTransform: 'none', color: '#8b5cf6' }}>Adicionar Produto</Button>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ textTransform: 'none' }}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}
            sx={{ bgcolor: '#8b5cf6', '&:hover': { bgcolor: '#7c3aed' }, textTransform: 'none', fontWeight: 600 }}>
            {saving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Criar Requisição'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ──── Dialog Detalhe + Interações ──── */}
      <Dialog fullScreen={isMobile} scroll="paper" open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="lg" fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
        {selectedReq && (() => {
          const st = STATUS_MAP[selectedReq.status] || STATUS_MAP.pending;
          const pr = PRIORITY_MAP[selectedReq.priority] || PRIORITY_MAP.normal;
          return (
            <>
              <Box sx={{ background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)', px: 3, py: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <RequisitionIcon sx={{ color: '#fff' }} />
                  <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1.1rem' }}>
                    Requisição #{selectedReq.number || selectedReq.id?.slice(0, 6)}
                  </Typography>
                </Box>
                <IconButton onClick={() => setDetailOpen(false)} sx={{ color: '#fff' }}><CloseIcon /></IconButton>
              </Box>

              <DialogContent sx={{ p: 0 }}>
                <Grid container sx={{ minHeight: 500 }}>
                  {/* Lado esquerdo - Dados da requisição */}
                  <Grid item xs={12} md={7} sx={{ p: 3, borderRight: { md: '1px solid #f1f5f9' } }}>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      <Grid item xs={6} sm={4}>
                        <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.68rem' }}>Solicitante</Typography>
                        <Typography sx={{ fontWeight: 500 }}>{selectedReq.requester_name}</Typography>
                      </Grid>
                      <Grid item xs={6} sm={4}>
                        <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.68rem' }}>Prioridade</Typography>
                        <Box><Chip label={pr.label} size="small" sx={{ fontWeight: 600, bgcolor: pr.bg, color: pr.color }} /></Box>
                      </Grid>
                      <Grid item xs={6} sm={4}>
                        <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.68rem' }}>Status</Typography>
                        <Box>
                          <FormControl size="small" sx={{ minWidth: 160 }}>
                            <Select value={selectedReq.status} onChange={(e) => handleStatusChange(selectedReq.id, e.target.value)}
                              sx={{ fontSize: '0.82rem', fontWeight: 600 }}>
                              {Object.entries(STATUS_MAP).map(([k, v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
                            </Select>
                          </FormControl>
                        </Box>
                      </Grid>
                      <Grid item xs={6} sm={4}>
                        <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.68rem' }}>Localização destino</Typography>
                        <Typography sx={{ fontWeight: 500 }}>{selectedReq.destination_location || '-'}</Typography>
                      </Grid>
                      <Grid item xs={6} sm={4}>
                        <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.68rem' }}>Prazo</Typography>
                        <Typography sx={{ fontWeight: 500 }}>{formatDate(selectedReq.deadline)}</Typography>
                      </Grid>
                      <Grid item xs={6} sm={4}>
                        <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.68rem' }}>Destinatário</Typography>
                        <Typography sx={{ fontWeight: 500 }}>{selectedReq.destination_name || '-'}</Typography>
                      </Grid>
                    </Grid>

                    <Divider sx={{ my: 2 }}><Chip label="Produtos" size="small" sx={{ fontWeight: 600 }} /></Divider>

                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: '#f9fafb' }}>
                            <TableCell sx={{ fontWeight: 700 }}>Produto</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Quantidade</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Unidade</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Obs</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(selectedReq.items || []).map((item, idx) => (
                            <TableRow key={idx}>
                              <TableCell><Typography variant="body2" sx={{ fontWeight: 500 }}>{item.product_name}</Typography></TableCell>
                              <TableCell>{item.quantity}</TableCell>
                              <TableCell>{item.unit || 'unidade'}</TableCell>
                              <TableCell>
                                <Chip label={item.status === 'fulfilled' ? 'Transferido' : 'Pendente'} size="small"
                                  sx={{ fontWeight: 600, fontSize: '0.7rem', bgcolor: item.status === 'fulfilled' ? '#dcfce7' : '#fef3c7', color: item.status === 'fulfilled' ? '#059669' : '#d97706' }} />
                              </TableCell>
                              <TableCell><Typography variant="caption">{item.notes || '-'}</Typography></TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>

                  {/* Lado direito - Interações */}
                  <Grid item xs={12} md={5} sx={{ display: 'flex', flexDirection: 'column', bgcolor: '#fafbfc' }}>
                    <Box sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid #f1f5f9' }}>
                      <Typography sx={{ fontWeight: 700, color: '#1e293b', fontSize: '0.95rem' }}>Interações</Typography>
                    </Box>

                    <Box sx={{ flex: 1, overflow: 'auto', px: 2.5, py: 1.5, maxHeight: 400 }}>
                      {(!selectedReq.interactions || selectedReq.interactions.length === 0) ? (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                          <Typography sx={{ color: '#94a3b8', fontSize: '0.85rem', bgcolor: '#e8f5e9', borderRadius: 2, p: 2 }}>
                            Nenhuma interação neste chamado.
                          </Typography>
                        </Box>
                      ) : (
                        selectedReq.interactions.map((inter) => (
                          <Box key={inter.id} sx={{ mb: 1.5, display: 'flex', gap: 1 }}>
                            <Avatar sx={{ width: 28, height: 28, bgcolor: '#8b5cf6', fontSize: '0.7rem' }}>
                              {(inter.user_name || 'U').charAt(0).toUpperCase()}
                            </Avatar>
                            <Box sx={{ flex: 1, bgcolor: '#fff', p: 1.5, borderRadius: 2, border: '1px solid #e8eef3' }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                <Typography sx={{ fontWeight: 600, fontSize: '0.78rem', color: '#1e293b' }}>{inter.user_name}</Typography>
                                <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8' }}>{formatDateTime(inter.created_at)}</Typography>
                              </Box>
                              <Typography sx={{ fontSize: '0.85rem', color: '#475569' }}>{inter.message}</Typography>
                            </Box>
                          </Box>
                        ))
                      )}
                    </Box>

                    <Box sx={{ px: 2.5, py: 1.5, borderTop: '1px solid #f1f5f9', display: 'flex', gap: 1 }}>
                      <TextField size="small" fullWidth placeholder="Digite sua mensagem para interação no chamado..."
                        value={newMessage} onChange={(e) => setNewMessage(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleSendInteraction()}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                      <Button variant="contained" onClick={handleSendInteraction} disabled={!newMessage.trim()}
                        sx={{ bgcolor: '#0891b2', '&:hover': { bgcolor: '#0e7490' }, minWidth: 40, px: 1.5 }}>
                        <SendIcon sx={{ fontSize: 18 }} />
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </DialogContent>
            </>
          );
        })()}
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: isMobile ? 'center' : 'right' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default InventoryRequisitions;
